import { CheckCircleIcon, CheckIcon, ChevronDownIcon } from "@heroicons/react/24/outline";
import {
    CalendarDate,
    formatDateBlock,
    formatTimeRange,
    IsoTimestamp,
    RegionId,
    Task,
} from "@sp-crm/core";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AddressDisplay } from "components/contact-display/address-display";
import { trpc } from "components/rpc/trpc";
import { ActivityChip } from "components/tasks/activity-chip";
import { useActivityTypeName } from "components/tasks/activity-type-name";
import { TaskDoneNote } from "components/tasks/v2/task-done-note";
import { Taskv2 } from "components/tasks/v2/types";
import { DeleteButton } from "components/ui/action-button";
import { Badge } from "components/ui/badge";
import { Checkbox } from "components/ui/checkbox";
import { ClampedText } from "components/ui/clamped-text";
import { CompletingListItem } from "components/ui/list-motion";
import { SecondaryCompactButton } from "components/ui/secondary-button";
import { AnimatePresence } from "framer-motion";
import {
    GetTasksv2Query,
    UpdateTaskPayload,
    useGetTasksv2Query,
    useUpdateTaskMutation,
} from "generated/graphql";
import { produce } from "immer";
import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useCurrentUserId, useRegionId } from "store/selectors/hooks";
import { agendaSlice } from "store/slices/agenda";
import { ApplicationState } from "store/state";
import { breakNewlines } from "util/text";
import { handleEvent } from "util/user-events";
import { CalendarEventDto } from "../visits/types";
import { VisitEditor } from "../visits/visit-editor";
import { isVisitPast } from "../visits/visit-timing";
import { AgendaVisitOutcomePanel } from "./agenda-visit-outcome-panel";

export { isVisitPast };

export const filterVisitsForToday = (
    visits: CalendarEventDto[],
    today: string,
): CalendarEventDto[] =>
    visits
        .filter(visit => visit.eventDate === today)
        .sort((a, b) => (a.eventTime ?? "").localeCompare(b.eventTime ?? ""));

export const bucketTasks = (
    tasks: Taskv2[],
): { overdue: Taskv2[]; dueToday: Taskv2[] } => {
    const mostRecentMidnight = new Date();
    mostRecentMidnight.setHours(0, 0, 0, 0);
    const midnightTonight = new Date();
    midnightTonight.setHours(23, 59, 59, 999);

    const overdue: Taskv2[] = [];
    const dueToday: Taskv2[] = [];
    for (const task of tasks) {
        const effectiveDate = Task.load(task.task).when();
        if (!effectiveDate) {
            continue;
        }
        if (effectiveDate < mostRecentMidnight) {
            overdue.push(task);
        } else if (effectiveDate < midnightTonight) {
            dueToday.push(task);
        }
    }
    return { overdue, dueToday };
};

export const AgendaHardLandscape: React.FC = () => {
    const regionId = useRegionId();
    const currentUserId = useCurrentUserId();
    const trpcClient = trpc.useTRPC();
    const dispatch = useDispatch();
    const sectionVisibility = useSelector(
        (state: ApplicationState) => state.agenda.sectionVisibility,
    );

    const today = CalendarDate.fromLocalDate(new Date());
    const start = today.toString();

    const completedAfter = useMemo(() => new Date().toISOString() as IsoTimestamp, []);
    const queryClient = useQueryClient();

    const queryParams = useMemo(
        () => ({
            params: {
                regionId: regionId ?? ("" as RegionId),
                completedAt: completedAfter,
                ownerId: currentUserId,
            },
        }),
        [regionId, completedAfter, currentUserId],
    );

    const tasksQuery = useGetTasksv2Query(queryParams, { enabled: !!regionId });

    const modifyLocalTask = useCallback(
        (updated: Taskv2) => {
            queryClient.setQueryData<GetTasksv2Query>(["getTasksv2", queryParams], data =>
                produce(data, draft => {
                    if (draft) {
                        const existing = draft.getTasksv2.find(
                            x => x.task.id === updated.task.id,
                        );
                        if (existing) {
                            existing.task = updated.task;
                            existing.entity = updated.entity;
                        }
                    }
                }),
            );
        },
        [queryClient, queryParams],
    );

    const visitsQuery = useQuery({
        ...trpcClient.calendarEvents.listForCalendar.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            startDate: start,
            endDate: start,
        }),
        enabled: !!regionId,
    });

    const updateTask = useUpdateTaskMutation();

    const { overdue, dueToday } = useMemo(
        () => bucketTasks(tasksQuery.data?.getTasksv2 ?? []),
        [tasksQuery.data],
    );

    const visits = useMemo(
        () => filterVisitsForToday(visitsQuery.data ?? [], start),
        [visitsQuery.data, start],
    );

    const toggleTask = async (taskv2: Taskv2) => {
        const done = !taskv2.task.done;
        if (done) {
            handleEvent("task-complete", { source: "my-agenda" });
        }
        const params: UpdateTaskPayload = {
            status: done ? "done" : "new",
            completedAt: done ? (new Date().toISOString() as IsoTimestamp) : null,
        };
        modifyLocalTask({
            task: { ...taskv2.task, ...params, done },
            entity: taskv2.entity,
        });
        await updateTask.mutateAsync({ taskId: taskv2.task.id, params });
        await tasksQuery.refetch();
    };

    const saveTaskNote = async (taskv2: Taskv2, notes: string) => {
        handleEvent("task-note-saved", { source: "my-agenda" });
        modifyLocalTask({ task: { ...taskv2.task, notes }, entity: taskv2.entity });
        await updateTask.mutateAsync({ taskId: taskv2.task.id, params: { notes } });
        await tasksQuery.refetch();
    };

    const total = overdue.length + dueToday.length + visits.length;
    if (total === 0) {
        return null;
    }

    return (
        <div className="overflow-hidden rounded-lg bg-white ring-1 ring-gray-300">
            <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-3">
                <span className="h-5 w-1 flex-shrink-0 rounded-full bg-brand-500" />
                <CheckCircleIcon className="h-4 w-4 flex-shrink-0 text-gray-600" />
                <span className="text-sm font-semibold text-gray-800">Today</span>
                <span className="text-xs text-gray-500">{total}</span>
            </div>
            <div className="divide-y divide-gray-100">
                {overdue.length > 0 ? (
                    <Group
                        label="Overdue"
                        tone="overdue"
                        count={overdue.length}
                        open={sectionVisibility.overdue}
                        onToggle={() =>
                            dispatch(agendaSlice.actions.toggleAgendaSection("overdue"))
                        }>
                        {overdue.map(task => (
                            <TaskRow
                                key={task.task.id}
                                task={task}
                                onToggle={toggleTask}
                                onSaveNote={saveTaskNote}
                            />
                        ))}
                    </Group>
                ) : null}
                {dueToday.length > 0 ? (
                    <Group
                        label="Due today"
                        tone="today"
                        count={dueToday.length}
                        open={sectionVisibility.dueToday}
                        onToggle={() =>
                            dispatch(agendaSlice.actions.toggleAgendaSection("dueToday"))
                        }>
                        {dueToday.map(task => (
                            <TaskRow
                                key={task.task.id}
                                task={task}
                                onToggle={toggleTask}
                                onSaveNote={saveTaskNote}
                            />
                        ))}
                    </Group>
                ) : null}
                {visits.length > 0 ? (
                    <Group
                        label="Scheduled visits"
                        tone="today"
                        count={visits.length}
                        open={sectionVisibility.scheduledVisits}
                        onToggle={() =>
                            dispatch(
                                agendaSlice.actions.toggleAgendaSection(
                                    "scheduledVisits",
                                ),
                            )
                        }>
                        {visits.map(visit => (
                            <VisitRow
                                key={visit.id}
                                visit={visit}
                                onChanged={() => visitsQuery.refetch()}
                            />
                        ))}
                    </Group>
                ) : null}
            </div>
        </div>
    );
};

const toneClasses: Record<"overdue" | "today", string> = {
    overdue: "text-red-600",
    today: "text-gray-500",
};

const toneBadgeType: Record<"overdue" | "today", "error" | "info"> = {
    overdue: "error",
    today: "info",
};

const Group: React.FC<{
    label: string;
    tone: "overdue" | "today";
    count: number;
    open: boolean;
    onToggle: () => void;
    children: React.ReactNode;
}> = ({ label, tone, count, open, onToggle, children }) => {
    return (
        <div>
            <button
                type="button"
                onClick={onToggle}
                className="flex w-full items-center gap-2 px-4 py-2 hover:bg-gray-50">
                <ChevronDownIcon
                    className={`h-4 w-4 flex-shrink-0 text-gray-400 transition-transform ${
                        open ? "rotate-0" : "-rotate-90"
                    }`}
                    aria-hidden="true"
                />
                <span
                    className={`text-[11px] font-bold uppercase tracking-wide ${toneClasses[tone]}`}>
                    {label}
                </span>
                <Badge value={count} type={toneBadgeType[tone]} />
            </button>
            {open ? (
                <ul>
                    <AnimatePresence initial={false}>{children}</AnimatePresence>
                </ul>
            ) : null}
        </div>
    );
};

const TaskRow: React.FC<{
    task: Taskv2;
    onToggle: (task: Taskv2) => void;
    onSaveNote: (task: Taskv2, notes: string) => Promise<void>;
}> = ({ task, onToggle, onSaveNote }) => {
    const dueLabel = taskDueLabel(task);
    const taskHref = task.entity?.path ?? null;
    const done = task.task.done;
    const textClass = `text-sm leading-tight ${
        done ? "text-gray-500 line-through" : "text-gray-900"
    }`;
    return (
        <CompletingListItem
            isDone={done}
            className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50">
            <div className="mt-0.5 w-5 flex-shrink-0">
                <Checkbox
                    checked={done}
                    onChange={() => onToggle(task)}
                    aria-label={done ? "Mark incomplete" : "Mark complete"}
                />
            </div>
            <div className="min-w-0 flex-1">
                {taskHref ? (
                    <Link to={taskHref} className={`${textClass} hover:underline`}>
                        {task.task.text}
                    </Link>
                ) : (
                    <div className={textClass}>{task.task.text}</div>
                )}
                <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-xs text-gray-500">
                    {dueLabel ? <span>{dueLabel}</span> : null}
                    {task.entity?.displayName ? (
                        <span>{task.entity.displayName}</span>
                    ) : null}
                </div>
                {done ? (
                    <div className="mt-1.5">
                        <TaskDoneNote
                            notes={task.task.notes}
                            onSaveNote={notes => onSaveNote(task, notes)}
                        />
                    </div>
                ) : null}
            </div>
        </CompletingListItem>
    );
};

const taskDueLabel = (task: Taskv2): string | null => {
    if (!task.task.dueDate) {
        return null;
    }
    const parsed = CalendarDate.parse(task.task.dueDate);
    return parsed.hasValue ? parsed.get().humanFriendly() : null;
};

export const VisitRow: React.FC<{ visit: CalendarEventDto; onChanged: () => void }> = ({
    visit,
    onChanged,
}) => {
    const trpcClient = trpc.useTRPC();
    const [recordingOutcome, setRecordingOutcome] = useState(false);
    const [editing, setEditing] = useState(false);
    const deleteMutation = useMutation({
        ...trpcClient.calendarEvents.delete.mutationOptions(),
        onSuccess: onChanged,
    });
    const typeName = useActivityTypeName(visit.activityTypeId);
    const dateBlock = formatDateBlock(visit.eventDate);
    const name =
        visit.parentContactName ??
        visit.businessName ??
        visit.communityName ??
        "Referral source";
    const orgSubtitle =
        visit.parentContactName &&
        visit.businessName &&
        visit.businessName !== visit.parentContactName
            ? visit.businessName
            : null;
    const address = visit.businessAddress ?? visit.communityAddress;
    const timeRange = visit.eventTime
        ? formatTimeRange(visit.eventTime, visit.eventDuration)
        : null;
    const parentIsBusiness =
        visit.businessId !== null && visit.businessId === visit.parentId;
    const visitHref = parentIsBusiness
        ? `/references/organizations/show/${visit.parentId}`
        : `/references/contacts/show/${visit.parentId}`;
    const label = visit.title?.trim() ? `${visit.title.trim()} · ${name}` : name;
    const completed = !!visit.outcome?.trim();
    return (
        <CompletingListItem
            isDone={completed}
            className="flex items-start gap-3 px-4 py-2.5 hover:bg-gray-50">
            {dateBlock ? (
                <div className="flex w-12 flex-shrink-0 flex-col items-center rounded-md border border-brand-200 bg-brand-50 px-1 py-1">
                    <div className="text-[10px] font-bold uppercase leading-tight text-brand-700">
                        {dateBlock.month}
                    </div>
                    <div className="text-base font-bold leading-tight text-brand-900">
                        {dateBlock.day}
                    </div>
                </div>
            ) : null}
            <div className="min-w-0 flex-1">
                <Link
                    to={visitHref}
                    className="text-sm font-medium leading-tight text-gray-900 hover:underline">
                    {label}
                </Link>
                {typeName ? (
                    <div className="mt-1">
                        <ActivityChip category="visit" label={typeName} />
                    </div>
                ) : null}
                {orgSubtitle ? (
                    <div className="mt-0.5 text-xs text-gray-500">{orgSubtitle}</div>
                ) : null}
                {timeRange ? (
                    <div className="mt-0.5 text-xs text-gray-500">{timeRange}</div>
                ) : null}
                {address ? (
                    <div className="mt-1 text-sm">
                        <AddressDisplay value={address} />
                    </div>
                ) : null}
                {visit.privateNotes?.trim() ? (
                    <p className="mt-1 text-xs text-gray-500">
                        <ClampedText lines={3}>
                            {breakNewlines(visit.privateNotes)}
                        </ClampedText>
                    </p>
                ) : null}
                {completed && visit.outcome?.trim() ? (
                    <div className="mt-1 text-xs text-gray-500">
                        Outcome: {visit.outcome}
                    </div>
                ) : null}
            </div>
            <div className="flex flex-shrink-0 items-center gap-1 self-center">
                {completed ? (
                    <span className="inline-flex items-center gap-1 rounded-full bg-green-50 px-2.5 py-0.5 text-xs font-medium text-green-700">
                        <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        Done
                    </span>
                ) : (
                    <SecondaryCompactButton
                        className="items-center gap-1"
                        onClick={() => setRecordingOutcome(true)}>
                        <CheckIcon className="h-3.5 w-3.5 flex-shrink-0" />
                        Log outcome
                    </SecondaryCompactButton>
                )}
                <SecondaryCompactButton onClick={() => setEditing(true)}>
                    Edit
                </SecondaryCompactButton>
                <DeleteButton
                    backgroundColor="bg-white"
                    onClick={() => deleteMutation.mutate({ id: visit.id })}
                    confirm={{
                        title: "Delete Visit",
                        message: "Are you sure you want to delete this visit?",
                    }}
                />
            </div>
            {recordingOutcome ? (
                <AgendaVisitOutcomePanel
                    visit={visit}
                    onChanged={onChanged}
                    onClose={() => setRecordingOutcome(false)}
                />
            ) : null}
            {editing ? (
                <VisitEditor
                    mode="edit"
                    visit={visit}
                    onSaved={onChanged}
                    onClose={() => setEditing(false)}
                />
            ) : null}
        </CompletingListItem>
    );
};
